import { Table, TableBody, TableCell, TableRow } from "@mui/material";
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import PATEditButton from "../../components/Buttons/PATEditButton";
import PATDeleteButton from "../../components/Buttons/PATDeleteButton";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { useDispatch, useSelector } from "react-redux";
import { updateOrCreateUserSetting } from "../../reducers/UserSettingsReducer";
import {
	getRowsPerPageOptions,
	StatusBadge,
	formatDate,
	formatCurrency,
} from "../../utils/functions";
import { Box } from "@mui/system";
import { useTheme } from "@emotion/react";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { AMSModules, paginationStyle } from "../../utils/constants";
import MaintenanceTableToolbar from "./MaintenanceTableToolbar";
import AddEditMaintenanceDialog from "./AddEditMaintenanceDialog";
import {
	getMaintenanceList,
	createMaintenance,
	updateMaintenance,
	deleteMaintenance,
} from "../../reducers/MaintenanceReducer";
import { notify } from "../../reducers/NotifierReducer";
import DeleteConfirmationDialog from "../../components/DeleteConfirmationDialog";
import { getEmployees, getFundingSources } from "../../utils/commonServices";
import { getEquipmentOptions } from "./useMaintenanceData";
import { getEquipmentServiceRequirementsOptions } from "../Equipments/useEquipmentsData";

const INITIAL_PAGE = 0;
const INITIAL_PAGE_SIZE = 20;
const INITIAL_SORT_MODEL = [{ field: "id", sort: "desc" }];

export const initialFilterState = {
	status: null,
	equipmentId: [],
	performedBy: [],
	perPersonnelId: [],
	fundingSourceId: [],
	period: null,
	from: null,
	to: null,
};

const MAINT_STATUSES = [
	"Scheduled",
	"In Progress",
	"Completed",
	"Cancelled",
	"Pending",
];

const Maintenance = () => {
	const history = useHistory();
	const dispatch = useDispatch();
	const theme = useTheme();

	const { maintenance, loading, total } = useSelector(
		(state) =>
			state?.maintenance || {
				maintenance: [],
				loading: false,
				total: 0,
			},
	);
	const userAgency = useSelector((state) => state.user.userAgency);
	const { userData, isAuthenticated } = useSelector((state) => state.user);
	const { userSettings } = useSelector((state) => state.userSettings);
	const themeMode = useSelector((state) => state.theme.mode);
	const filtersInfo = useSelector((state) => state.filtersInfo);
	const dictionary = useSelector((state) => state.dictionary.dictionary);

	const [search, setSearch] = useState("");
	const [openDialog, setOpenDialog] = useState(false);
	const [columnsOrder, setColumnsOrder] = useState([]);
	const [columnsSize, setColumnsSize] = useState([]);
	const [settingsLoaded, setSettingsLoaded] = useState(false);
	const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
	const [page, setPage] = useState(INITIAL_PAGE);
	const [pageSize, setPageSize] = useState(INITIAL_PAGE_SIZE);
	const [sortModel, setSortModel] = useState(INITIAL_SORT_MODEL);
	const [filters, setFilters] = useState(
		filtersInfo?.maintenance || initialFilterState,
	);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [selectedId, setSelectedId] = useState(null);
	const [selectedRecord, setSelectedRecord] = useState(null);
	const [employees, setEmployees] = useState([]);
	const [fundingSources, setFundingSources] = useState([]);
	const [equipmentOptions, setEquipmentOptions] = useState([]);
	const [serviceRequirements, setServiceRequirements] = useState([]);

	const currentAgency = useRef(null);

	const statusOptions = MAINT_STATUSES.map((s) => ({
		label: s,
		value: s,
	}));

	const handleLoadData = async () => {
		const [empList, fundingList, equipList, srList] = await Promise.all([
			getEmployees(userAgency, true),
			getFundingSources(userAgency, true),
			getEquipmentOptions(userAgency),
			getEquipmentServiceRequirementsOptions(dictionary),
		]);

		setEmployees(empList);
		setFundingSources(fundingList);
		setEquipmentOptions(equipList);
		setServiceRequirements(srList);
	};

	useEffect(() => {
		if (userAgency && dictionary) {
			handleLoadData();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userAgency, dictionary]);

	useEffect(() => {
		if (!settingsLoaded || !isAuthenticated) return;

		const { field, sort } = sortModel[0] || {};
		if (field && sort) {
			const isAgencyChanged = currentAgency.current !== userAgency;
			if (isAgencyChanged) {
				resetFilters();
				currentAgency.current = userAgency;
			}

			dispatch(
				getMaintenanceList(
					page,
					pageSize,
					field,
					sort,
					search,
					isAgencyChanged ? initialFilterState : filters,
					userAgency,
				),
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		dispatch,
		page,
		pageSize,
		sortModel,
		search,
		filters,
		settingsLoaded,
		isAuthenticated,
		userAgency,
	]);

	const getEquipmentDisplay = useCallback(
		(equipmentId) => {
			const eq = equipmentOptions.find((e) => e.value === equipmentId);
			return eq ? eq.label : "—";
		},
		[equipmentOptions],
	);

	const getEmployeeDisplay = useCallback(
		(employeeId) => {
			const emp = employees.find(
				(e) => e.value === employeeId || e.value === String(employeeId),
			);
			return emp ? emp.label : "—";
		},
		[employees],
	);

	const baseColumns = useMemo(
		() => [
			{
				field: "date",
				headerName: "Date",
				width: 120,
				sortable: true,
				renderCell: ({ row }) => formatDate(row?.date),
			},
			{
				field: "description",
				headerName: "Description",
				width: 200,
				sortable: true,
				renderCell: ({ row }) => row?.description || "—",
			},
			{
				field: "performedBy",
				headerName: "Performed By",
				width: 150,
				sortable: true,
			},
			{
				field: "perPersonnelId",
				headerName: "Assigned To",
				width: 150,
				sortable: true,
				renderCell: ({ row }) => getEmployeeDisplay(row.perPersonnelId),
			},
			{
				field: "equipmentId",
				headerName: "Equipment",
				width: 200,
				sortable: true,
				renderCell: ({ row }) => getEquipmentDisplay(row.equipmentId),
			},
			{
				field: "cost",
				headerName: "Cost",
				width: 100,
				sortable: true,
				renderCell: ({ row }) =>
					row?.cost ? formatCurrency(row.cost) : "—",
			},
			{
				field: "status",
				headerName: "Status",
				width: 130,
				sortable: true,
				renderCell: ({ row }) => <StatusBadge status={row?.status} />,
			},
			{
				field: "statusDate",
				headerName: "Status Date",
				width: 120,
				sortable: true,
				renderCell: ({ row }) =>
					row?.statusDate ? formatDate(row.statusDate) : "—",
			},
			{
				field: "actions",
				headerName: "Actions",
				type: "actions",
				width: 120,
				align: "center",
				sortable: false,
				renderCell: ({ row }) =>
					row.id ? (
						<Table>
							<TableBody>
								<TableRow>
									<TableCell
										align="right"
										sx={{ padding: 0.5 }}
									>
										<PATEditButton
											tooltipText="Edit Maintenance"
											onClick={() => {
												setSelectedRecord(row);
												setOpenDialog(true);
											}}
											size="20px"
										/>
									</TableCell>
									<TableCell
										align="right"
										sx={{ padding: 0.5 }}
									>
										<PATDeleteButton
											tooltipText="Delete Maintenance"
											onClick={() => {
												setSelectedId(row.id);
												setSelectedRecord(row);
												setOpenDeleteDialog(true);
											}}
											size="20px"
										/>
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					) : null,
			},
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[history, getEquipmentDisplay, getEmployeeDisplay],
	);

	const columns = useMemo(() => {
		const hasOrder = columnsOrder.length > 0;
		const hasSizes = Object.keys(columnsSize).length > 0;

		if (!hasOrder && !hasSizes) return baseColumns;

		let baseOrderedColumns;

		if (hasOrder) {
			const ordered = columnsOrder
				.map((field) => baseColumns.find((col) => col.field === field))
				.filter(Boolean);

			const remaining = baseColumns.filter(
				(col) => !columnsOrder.includes(col.field),
			);

			const actionsIndex = ordered.findIndex(
				(col) => col.field === "actions",
			);
			const insertAt = actionsIndex > -1 ? actionsIndex : ordered.length;

			baseOrderedColumns = [
				...ordered.slice(0, insertAt),
				...remaining,
				...ordered.slice(insertAt),
			];
		} else {
			baseOrderedColumns = baseColumns;
		}

		return baseOrderedColumns.map((col) => ({
			...col,
			width: columnsSize[col.field] ?? col.width ?? 100,
		}));
	}, [baseColumns, columnsOrder, columnsSize]);

	const allColumnFields = baseColumns.map((col) => col.field);

	useEffect(() => {
		const defaultVisibility = {};
		currentAgency.current = userAgency;
		allColumnFields.forEach((field) => {
			defaultVisibility[field] = true;
		});
		setColumnVisibilityModel((prev) => ({
			...defaultVisibility,
			...prev,
		}));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!isAuthenticated || settingsLoaded) return;

		const initialize = async () => {
			const settingsMap = Object.fromEntries(
				userSettings.map((s) => [s.key, s.value]),
			);
			if (settingsMap["maintenanceTableColumnOrder"]) {
				setColumnsOrder(
					JSON.parse(settingsMap["maintenanceTableColumnOrder"]),
				);
			}
			if (settingsMap["maintenanceTableColumnSizes"]) {
				const widthArray = JSON.parse(
					settingsMap["maintenanceTableColumnSizes"],
				);
				setColumnsSize(
					widthArray.reduce((acc, curr) => {
						acc[curr.field] = curr.width;
						return acc;
					}, {}),
				);
			}
			if (settingsMap["maintenanceTableColumnVisibility"]) {
				setColumnVisibilityModel(
					JSON.parse(settingsMap["maintenanceTableColumnVisibility"]),
				);
			}
			if (settingsMap["maintenanceTableSortModel"]) {
				const saved = JSON.parse(
					settingsMap["maintenanceTableSortModel"],
				);
				if (Array.isArray(saved)) {
					const valid = saved.filter((m) =>
						baseColumns.some((col) => col.field === m.field),
					);
					if (valid.length > 0) setSortModel(saved);
				}
			}
			setSettingsLoaded(true);
		};

		initialize();
	}, [isAuthenticated, userSettings, settingsLoaded, baseColumns]);

	const handleSortModelChange = useCallback(
		(model) => {
			const newSortModel = model.length > 0 ? model : INITIAL_SORT_MODEL;
			if (JSON.stringify(newSortModel) !== JSON.stringify(sortModel)) {
				setSortModel(newSortModel);
				setPage(INITIAL_PAGE);
				if (userData?.user?.ptsUserID) {
					dispatch(
						updateOrCreateUserSetting(
							userData.user.ptsUserID,
							"maintenanceTableSortModel",
							JSON.stringify(newSortModel),
						),
					);
				}
			}
		},
		[sortModel, dispatch, userData?.user?.ptsUserID],
	);

	const handleColumnOrderChange = useCallback(
		(params, event, details) => {
			const newColumnOrder = details.api
				.getAllColumns()
				.map((col) => col.field);
			if (userData?.user?.ptsUserID) {
				dispatch(
					updateOrCreateUserSetting(
						userData.user.ptsUserID,
						"maintenanceTableColumnOrder",
						JSON.stringify(newColumnOrder),
					),
				);
			}
		},
		[dispatch, userData?.user?.ptsUserID],
	);

	const handleColumnWidthChange = (params, event, details) => {
		const columnSizes = details.api.getAllColumns().map((col) => ({
			field: col.field,
			width: col.width,
		}));
		if (userData?.user?.ptsUserID) {
			dispatch(
				updateOrCreateUserSetting(
					userData.user.ptsUserID,
					"maintenanceTableColumnSizes",
					JSON.stringify(columnSizes),
				),
			);
		}
	};

	const handleColumnVisibilityModelChange = useCallback(
		(newModel) => {
			setColumnVisibilityModel(newModel);
			if (userData?.user?.ptsUserID) {
				dispatch(
					updateOrCreateUserSetting(
						userData.user.ptsUserID,
						"maintenanceTableColumnVisibility",
						JSON.stringify(newModel),
					),
				);
			}
		},
		[dispatch, userData?.user?.ptsUserID],
	);

	const rowsPerPageOptions = useMemo(
		() => getRowsPerPageOptions(total),
		[total],
	);

	const handleOnFilterChange = (newFilters) => {
		setFilters(newFilters);
		dispatch({
			type: AMSModules.Maintenance,
			payload: newFilters,
		});
	};

	const resetFilters = () => {
		setFilters(initialFilterState);
		dispatch({
			type: AMSModules.Maintenance,
			payload: initialFilterState,
		});
	};

	const handleSave = async (payload) => {
		try {
			if (selectedRecord) {
				await dispatch(
					updateMaintenance(
						selectedRecord.id,
						payload,
						userData.user.ptsUserID,
						userAgency,
						selectedRecord,
					),
				);
			} else {
				await dispatch(
					createMaintenance(
						payload,
						userAgency,
						userData.user.ptsUserID,
					),
				);
			}
			setOpenDialog(false);
			setSelectedRecord(null);
			dispatch(
				notify("Maintenance record saved successfully", "success"),
			);
		} catch (error) {
			dispatch(notify("Failed to save maintenance record", "error"));
		}
	};

	const handleDelete = async () => {
		try {
			await dispatch(
				deleteMaintenance(
					selectedId,
					userData.user.ptsUserID,
					userAgency,
					selectedRecord,
				),
			);
			setOpenDeleteDialog(false);
			setSelectedId(null);
			setSelectedRecord(null);
		} catch (error) {
			dispatch(notify("Failed to delete maintenance record", "error"));
		}
	};

	// Print data
	const printMaintenance = useMemo(() => {
		return maintenance.map((m) => ({
			...m,
			dateFormatted: formatDate(m.date),
			statusDateFormatted: m.statusDate ? formatDate(m.statusDate) : "—",
			costFormatted: m.cost ? formatCurrency(m.cost) : "—",
			equipment: getEquipmentDisplay(m.equipmentId),
			performedByDisplay: getEmployeeDisplay(m.performedBy),
			assignedTo: getEmployeeDisplay(m.perPersonnelId),
		}));
	}, [maintenance, getEquipmentDisplay, getEmployeeDisplay]);

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "calc(100vh - 85px)",
				marginTop: theme.spacing(0.5),
			}}
		>
			<MaintenanceTableToolbar
				onAddClick={() => {
					setSelectedRecord(null);
					setOpenDialog(true);
				}}
				equipmentOptions={equipmentOptions}
				employeeOptions={employees}
				fundingOptions={fundingSources}
				statusOptions={statusOptions}
				search={search}
				setSearch={setSearch}
				maintenance={printMaintenance}
				resetFilters={resetFilters}
				filters={filters}
				onFilterChange={handleOnFilterChange}
			/>

			<Box
				sx={{
					flex: 1,
					overflow: "hidden",
					display: "flex",
					flexDirection: "column",
				}}
			>
				<DataGridPro
					rows={maintenance}
					columns={columns}
					initialState={{
						pinnedColumns: { right: ["actions"] },
					}}
					getRowId={(row) => row.GUID || row.id}
					columnVisibilityModel={columnVisibilityModel}
					onColumnVisibilityModelChange={
						handleColumnVisibilityModelChange
					}
					pageSize={pageSize}
					rowsPerPageOptions={rowsPerPageOptions}
					pagination
					paginationMode="server"
					sortModel={sortModel}
					sortingMode="server"
					onSortModelChange={handleSortModelChange}
					rowCount={total}
					page={page}
					onPageChange={setPage}
					onPageSizeChange={setPageSize}
					loading={loading}
					disableSelectionOnClick
					onColumnOrderChange={handleColumnOrderChange}
					onColumnWidthChange={handleColumnWidthChange}
					sx={{
						...paginationStyle(theme, themeMode),
						color: theme.palette.colorFont,
						margin: 1,
					}}
				/>
			</Box>

			<AddEditMaintenanceDialog
				open={openDialog}
				onClose={() => {
					setOpenDialog(false);
					setSelectedRecord(null);
				}}
				onSave={handleSave}
				initialData={selectedRecord}
				employeeOptions={employees}
				equipmentOptions={equipmentOptions}
				serviceRequirements={serviceRequirements || []}
				fundingSourceOptions={fundingSources}
			/>

			<DeleteConfirmationDialog
				open={openDeleteDialog}
				onClose={() => {
					setOpenDeleteDialog(false);
					setSelectedId(null);
					setSelectedRecord(null);
				}}
				onConfirm={handleDelete}
				title="Delete Maintenance Record"
				message="Are you sure you want to delete this maintenance record?"
			/>
		</Box>
	);
};

export default Maintenance;
